import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Subscription, finalize, take } from 'rxjs';

// Serviços
import { ContractService } from 'src/app/modules/services/contracts/contract.service';
import { LoadingService } from '../../loading/loading.service';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { AuthService } from 'src/app/modules/services/auth/auth.service';
import { UserService } from 'src/app/modules/services/user/user.service';

// Componentes
import { ModalComponent } from '../../modal/modal.component';
import { ModalFiltersComponent } from 'src/app/modules/shared/modals/modal-filters/modal-filters.component';
import { ModalHistoricComponent } from 'src/app/modules/shared/modals/modal-historic/modal-historic.component';

@Component({
  selector: 'app-list-contracts',
  templateUrl: './list-contracts.component.html',
  styleUrls: ['./list-contracts.component.scss']
})
export class ListContractsComponent implements OnInit, OnDestroy {
  // Configurações da Tabela
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];

  columnConfig = [
    { columnDef: 'tipo', header: 'Tipo', property: 'contractType.name' },
    { columnDef: 'N° Contrato', header: 'N° Contrato', property: 'contractNumber' },
    { columnDef: 'entidade', header: 'Entidade', property: 'company.razaoSocial' },
    { columnDef: 'dataInicio', header: 'Data Inicio', property: 'startDate', customTd: true },
    { columnDef: 'dataFinal', header: 'Data Final', property: 'endDate', customTd: true },
    { columnDef: 'status', header: 'Status', property: 'status', customTd: true },
    { columnDef: 'actions', header: 'Ações', custom: true }
  ];

  arrayStatus: any = [
    { label: "Ativo", property: "true", selected: false },
    { label: "Inativo", property: "false", selected: false },
  ];

  // Controle de Paginação e Ordenação
  pageSize: number = 10;
  pageIndex: number = 0;
  totalElements: number = 0;
  totalPages: number = 1;
  sortState: any = { sortProperty: 'criadoEm', sortDirection: 'desc' };

  // Filtros
  filterTextValue: string = '';
  statusFilter: string = '';
  filterData: any;
  criterios: any = [];
  criteriosLabels!: { [key: string]: string; };
  form!: FormGroup;

  // Permissões
  buttonInclusaoDisabled: boolean = false;
  buttonExclusaoDisabled: boolean = false;
  buttonEdicaoDisabled: boolean = false;
  currentRoute: string = '';

  private subsctiptions: Subscription[] = [];

  constructor(
    private router: Router,
    private contractService: ContractService,
    private loadingService: LoadingService,
    public toastService: ToastService,
    public dialog: MatDialog,
    private modalCtrl: ModalController,
    private authService: AuthService,
    private userService: UserService,
    private fb: FormBuilder,
  ) {
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this.displayedColumns = this.columnConfig.map(col => col.columnDef);
    this.initForm();
    this.buildCriteriosLabels();
    this.loadUserInfo();
    this.onGetContract();
  }

  private initForm() {
    this.form = this.fb.group({
      dataEficaciaInicio: [''],
      dataEficaciaFinal: [''],
      dataAssinaturaInicio: [''],
      dataAssinaturaFinal: [''],
      medidaProrrogavelAte: [''],

      prorrogavelAte: [''],
      dataInicio: [''],
      dataFinal: [''],
      entidade: [''],
      tipoContract: ['']
    });
  }

  private buildCriteriosLabels() {
    this.criteriosLabels = {
      dataEficaciaInicio: 'Eficácia (Início)',
      dataEficaciaFinal: 'Eficácia (Final)',
      dataAssinaturaInicio: 'Assinatura (Início)',
      dataAssinaturaFinal: 'Assinatura (Final)',
      medidaProrrogavelAte: 'Medida Prorrogação',

      prorrogavelAte: 'Prorrogável Até',
      dataInicio: 'Data Início',
      dataFinal: 'Data Final',
      entidade: 'Entidade',
      tipoContract: 'Tipo Contrato'
    };
  }

  onGetContract(pageIndex: number = 0) {
    this.loadingService.showLoading();
    const f = this.filterData?.value || {};

    this.contractService.getContracts(
      pageIndex,
      this.sortState.sortDirection,
      this.sortState.sortProperty,
      f.tipoContract || '',
      f.entidade || '',
      this.filterTextValue,
      f.dataInicio || '',
      f.dataFinal || '',
      this.statusFilter,
      this.filterTextValue,
      '', // editalNumero (adicionar ao form se necessário)
      f.prorrogavelAte || '',
      f.medidaProrrogavelAte || '',
      f.dataAssinaturaInicio || '',
      f.dataAssinaturaFinal || '',
      f.dataEficaciaInicio || '',
      f.dataEficaciaFinal || '',
      '',
      this.pageSize,
    )
    .pipe(
      take(1),
      finalize(() => this.loadingService.hideLoading())
    )
    .subscribe({
      next: (res: any) => {
        this.dataSource.data = res.content;
        this.pageSize = res.pageSize;
        this.totalPages = res.totalPages;
        this.totalElements = res.totalElements;
        this.pageIndex = res.pageNumber;
      },
      error: (erro: any) => {
        this.dataSource.data = [];
        if (erro.status !== 404) this.toastService.showError(erro.error.message);
      }
    });
  }

  // === EVENTOS TABLE-DEFAULT ===

  onSortChange(sortProperty: any): void {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection = this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }
    this.onGetContract(0);
  }

  pageChangeEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.onGetContract(event.pageIndex);
  }

  applyFilter(filterValue: string) {
    this.filterTextValue = filterValue;
    this.onGetContract(0);
  }

  filterStatusChanged(event: any) {
    this.statusFilter = event[0]?.property || '';
    this.onGetContract(0);
  }

  openModalFilters() {
    const props = this.filterData ? this.filterData : this.generatePropsFromForm(this.form);
    const data = { type: 'contract', propsContract: props };

    const dialogRef = this.dialog.open(ModalFiltersComponent, {
      panelClass: ['filter-panel'],
      data,
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {

        this.filterData = res;
        this.criterios = this.getCriterios(res);
        this.onGetContract(0);
      }
    });
  }

  // === AUXILIARES DE FILTRO ===

  private generatePropsFromForm(form: FormGroup): any {
    const props: any = {};
    Object.keys(form.controls).forEach(key => {
      const val = form.get(key)?.value;
      if (val) props[key] = val;
    });
    return props;
  }

  getCriterios(data: any) {
    const props = this.generatePropsFromForm(data);
    return Object.keys(props);
  }

  criterioRemoved(event: any) {
    this.filterData.patchValue({ [event.criterio]: null });
    this.criterios = this.criterios.filter((c: any) => c !== event.criterio);
    this.onGetContract(0);
  }

  // === AÇÕES E NAVEGAÇÃO ===

  create() { this.router.navigate(['/criar-contrato']); }

  redirectToEditPage(id: any) { this.router.navigate([`contrato/${id}/editar`]); }

  redirectToDetailPage(id: any) { this.router.navigate([`contrato/${id}/detalhes`]); }

  openDeleteDialog(id: any) {
    this.openConfirmDialog('Excluir', 'Deseja excluir este contrato?', () => {
      this.contractService.deleteContract(id).subscribe(() => {
        this.toastService.showSuccess('Contrato deletado.');
        this.onGetContract();
      });
    });
  }

  openActivateDialog(id: any) {
    this.openConfirmDialog('Ativar', 'Deseja ativar este contrato?', () => {
      this.contractService.activate(id).subscribe(() => {
        this.toastService.showSuccess('Contrato ativado.');
        this.onGetContract();
      });
    });
  }

  openInactivateDialog(id: any) {
    this.openConfirmDialog('Inativar', 'Deseja inativar este contrato?', () => {
      this.contractService.inactivate(id).subscribe(() => {
        this.toastService.showSuccess('Contrato inativado.');
        this.onGetContract();
      });
    });
  }

  private openConfirmDialog(title: string, content: string, action: Function) {
    this.dialog.open(ModalComponent, {
      data: { modalTitle: title, modalContent: content, onPerformAction: action },
    });
  }

  async openDialogHistoricoContrato(id: any) {
    const data = {
      type: 'contract',
      id: id,
      fieldMappings: {
        contractNumber: 'Número do Contrato',
        company: 'Concessionária',
        status: 'Status',
        endDate: 'Data Final'
      }
    };
    const modal = await this.modalCtrl.create({
      component: ModalHistoricComponent,
      componentProps: { data, fieldMappings: data.fieldMappings },
    });
    modal.present();
  }

  // === SEGURANÇA E PERMISSÕES ===

  loadUserInfo() {
    const userId = this.authService.getUserIdFromToken();
    if (userId) {
      this.userService.getMenuUsuario(userId).subscribe((res: any) => {
        res.profileUser.permissaoPerfis.forEach((p: any) => this.checkFunctionality(p.funcionalidade));
      });
    }
  }

  checkFunctionality(f: any) {
    const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (f.url === this.currentRoute || norm(f.nome).includes('contrato')) {
      this.handleRestrictions(f.restricoes);
    }
    f.subFuncionalidades?.forEach((s: any) => this.checkFunctionality(s));
  }

  handleRestrictions(rest: any[]) {
    rest.forEach(r => {
      if (r.nome === 'Inclusão') this.buttonInclusaoDisabled = true;
      if (r.nome === 'Exclusão') this.buttonExclusaoDisabled = true;
      if (r.nome === 'Edição') this.buttonEdicaoDisabled = true;
    });
  }

  ngOnDestroy() { this.subsctiptions.forEach(s => s.unsubscribe()); }
}
